/**
 * RMSCommentEditor.jsx
 *
 * Google Docs-style commenting on top of ReactQuill, designed for use inside
 * a police RMS narrative editor. Supports two roles (supervisor and officer),
 * an immutable audit trail per state transition, character-anchored
 * highlights that survive document edits, a fixed-width sidebar with
 * filter chips, and full keyboard / ARIA support.
 *
 * Public props are documented via PropTypes at the bottom of this file.
 * Test hooks are exposed via React ref (`_test_*` prefixed). They are safe
 * to ignore in production usage.
 *
 * @version 1.0.0
 * @license MIT
 */

import React, {
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
} from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { AgencyContext } from '@/Context/Agency';
import {
  formatAgencyTimelineDateTime,
  getAgencyWallClockTimestamp,
} from '@/Components/Common/Utility';

/** Character count badge shown beneath comment/reply/edit textareas. */
function CharCount({ value, max }) {
  const len = String(value || '').length;
  const cls =
    len > max
      ? 'rms-comment-char-count rms-comment-char-count--over'
      : len >= max * 0.85
      ? 'rms-comment-char-count rms-comment-char-count--warn'
      : 'rms-comment-char-count';
  return <div className={cls}>{len}/{max}</div>;
}

/* ---------------------------------------------------------------------------
 * Constants
 * ------------------------------------------------------------------------- */

export const ROLE_SUPERVISOR = 'supervisor';
export const ROLE_OFFICER = 'officer';

export const STATUS_OPEN = 'open';
export const STATUS_ACCEPTED = 'accepted';
export const STATUS_RESOLVED = 'resolved';

export const ACTION_CREATE = 'create';
export const ACTION_REPLY = 'reply';
export const ACTION_ACCEPT = 'accept';
export const ACTION_RESOLVE = 'resolve';
export const ACTION_REOPEN = 'reopen';
export const ACTION_EDIT = 'edit';
export const ACTION_DELETE = 'delete';

export const FILTER_ALL = 'all';
export const FILTER_OPEN = STATUS_OPEN;
export const FILTER_ACCEPTED = STATUS_ACCEPTED;
export const FILTER_RESOLVED = STATUS_RESOLVED;

const STYLE_TAG_ID = 'rms-comment-system-styles';
const BLOT_NAME = 'rms-comment';
const BLOT_CLASS = 'rms-comment-highlight';
const KEYBOARD_OPEN_COMMENT = { ctrl: true, shift: true, key: 'M' };
const COMMENT_MAX_LENGTH = 100;

/* ---------------------------------------------------------------------------
 * One-time stylesheet injection (guarded against double-inject in HMR)
 * ------------------------------------------------------------------------- */

const STYLESHEET = `
.rms-comment-root {
  display: flex;
  flex-direction: row;
  gap: 12px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  font-size: 14px;
  color: #1f2937;
  width: 100%;
  box-sizing: border-box;
}
.report-review .quill {
  height: stretch;
  min-height: stretch;
}
.report-review .quill .ql-container {
  height: stretch;
  min-height: stretch;
}
.report-review .quill .ql-container .ql-editor {
  min-height: stretch;
}
.rms-comment-editor-wrap {
  flex: 1 1 auto;
  min-width: 0;
  position: relative;
}
// .rms-comment-editor-wrap .ql-editor {
//   min-height: 220px;
// }
.rms-comment-highlight {
  background-color: #fff3bf;
  border-bottom: 2px solid #f59f00;
  cursor: pointer;
  padding: 1px 0;
}
.rms-comment-highlight.rms-comment-highlight--active {
  background-color: #ffd43b;
  outline: 2px solid #f08c00;
  outline-offset: 1px;
}
.rms-comment-highlight.rms-comment-highlight--accepted {
  background-color: #d3f9d8;
  border-bottom-color: #2f9e44;
}
.rms-comment-highlight.rms-comment-highlight--resolved {
  background-color: #e9ecef;
  border-bottom-color: #adb5bd;
  text-decoration: line-through;
  text-decoration-color: #868e96;
}
.rms-comment-floating-btn {
  position: absolute;
  z-index: 50;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background-color: #1c7ed6;
  color: #fff;
  border: 1px solid #1864ab;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}
.rms-comment-floating-btn:hover {
  background-color: #1864ab;
}
.rms-comment-floating-btn:focus {
  outline: 2px solid #4dabf7;
  outline-offset: 2px;
}
.rms-comment-popover {
  position: fixed;
  z-index: 9999;
  width: 320px;
  background-color: #ffffff;
  border: 1px solid #ced4da;
  border-radius: 6px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.22);
  padding: 12px;
}
.rms-comment-popover-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}
.rms-comment-popover-title {
  font-weight: 600;
  font-size: 13px;
  margin: 0;
}
.rms-comment-popover-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  color: #6c757d;
  font-size: 16px;
  line-height: 1;
  flex-shrink: 0;
}
.rms-comment-popover-close:hover {
  background: #f1f3f5;
  color: #212529;
}
.rms-comment-popover-excerpt {
  background-color: #fff9db;
  border-left: 3px solid #f59f00;
  padding: 6px 8px;
  font-size: 12px;
  margin-bottom: 8px;
  border-radius: 2px;
  word-break: break-word;
  max-height: 80px;
  overflow-y: auto;
}
.rms-comment-popover textarea,
.rms-comment-reply-input {
  width: 100%;
  min-height: 64px;
  padding: 6px 8px;
  font-size: 13px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-family: inherit;
  box-sizing: border-box;
  resize: vertical;
}
.rms-comment-popover-buttons,
.rms-comment-reply-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
  margin-top: 8px;
}
.rms-comment-char-count {
  font-size: 11px;
  color: #868e96;
  text-align: right;
  margin-top: 3px;
  margin-bottom: 2px;
  line-height: 1.2;
}
.rms-comment-char-count--warn {
  color: #e67700;
  font-weight: 600;
}
.rms-comment-char-count--over {
  color: #c92a2a;
  font-weight: 700;
}
.rms-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 10px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 4px;
  border: 1px solid transparent;
  background: #f1f3f5;
  color: #212529;
  cursor: pointer;
}
.rms-btn:hover:not(:disabled) {
  background: #dee2e6;
}
.rms-btn:focus {
  outline: 2px solid #4dabf7;
  outline-offset: 1px;
}
.rms-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.rms-btn--primary {
  background: #1c7ed6;
  color: #fff;
  border-color: #1864ab;
}
.rms-btn--primary:hover:not(:disabled) {
  background: #1864ab;
}
.rms-btn--success {
  background: rgb(0, 31, 63);
  color: #fff;
  border-color: rgb(0, 31, 63);
}
.rms-btn--success:hover:not(:disabled) {
  color: rgb(0, 31, 63) !important;
  opacity: 0.8 !important;
}
.rms-btn--warning {
  background:#f59f00 !important;
  color: #fff !important;
  border-color: #f59f00 !important;
}
.rms-btn--warning:hover:not(:disabled) {
  background:rgba(245, 159, 0, 0.15) !important;
  color: #f59f00 !important;
  border-color: #f59f00 !important;
}
.rms-btn--danger {
  background: #e03131 !important;
  color: #fff !important;
  border-color: #c92a2a !important;
}
.rms-btn--danger:hover:not(:disabled) {
  background: #c92a2a !important;
}
.rms-btn--ghost {
  background: transparent;
  border-color: #ced4da;
}
.rms-comment-sidebar {
  flex: 0 0 300px;
  max-width: 300px;
  display: flex;
  flex-direction: column;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  background: #f8f9fa;
  max-height: 70vh;
  min-height: 10em !important;
  overflow: hidden;
}
.rms-comment-filter-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 8px;
  border-bottom: 1px solid #dee2e6;
  background: #fff;
}
.rms-comment-filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid #ced4da;
  background: #fff;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  color: #495057;
}
.rms-comment-filter-chip[aria-pressed="true"] {
  background: #1c7ed6;
  border-color: #1864ab;
  color: #fff;
}
.rms-comment-filter-chip .rms-comment-filter-count {
  background: rgba(0, 0, 0, 0.1);
  padding: 1px 6px;
  border-radius: 999px;
  font-size: 11px;
}
.rms-comment-filter-chip[aria-pressed="true"] .rms-comment-filter-count {
  background: rgba(255, 255, 255, 0.25);
}
.rms-comment-cards {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.rms-comment-empty {
  padding: 24px 12px;
  text-align: center;
  color: #868e96;
  font-size: 13px;
}
.rms-comment-card {
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 10px;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.rms-comment-card:hover {
  border-color: #adb5bd;
}
.rms-comment-card--active {
  border-color: #1c7ed6;
  box-shadow: 0 0 0 2px rgba(28, 126, 214, 0.2);
}
.rms-comment-card--accepted {
  border-left: 4px solid #2f9e44;
}
.rms-comment-card--resolved {
  border-left: 4px solid #2f9e44;
  background: #e9f9ee;
  opacity: 1;
}
.rms-comment-card--resolved .rms-comment-status-pill--resolved {
  background: #1f7a3a;
  color: #fff;
}
.rms-comment-card--resolved .rms-comment-quote {
  background: #d8f3df;
  border-left-color: #2f9e44;
}
.rms-comment-card--resolved .rms-comment-reply {
  background: #eceff3;
}
.rms-comment-card--resolved .rms-btn.rms-btn--ghost {
  background: #fff;
  border-color: #adb5bd;
}
.rms-comment-card--open {
  border-left: 4px solid #f59f00;
}
.rms-comment-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 6px;
  margin-bottom: 6px;
}
.rms-comment-author {
  font-weight: 600;
  font-size: 13px;
}
.rms-comment-meta {
  font-size: 11px;
  color: #868e96;
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}
.rms-comment-status-pill {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.rms-comment-status-pill--open { background: #fff3bf; color: #8a6d00; }
.rms-comment-status-pill--accepted { background: #d3f9d8; color: #2b8a3e; }
.rms-comment-status-pill--resolved { background: #e9ecef; color: #495057; }
.rms-comment-quote {
  background: #fff9db;
  border-left: 3px solid #f59f00;
  padding: 4px 6px;
  font-size: 12px;
  margin: 6px 0;
  border-radius: 2px;
  font-style: italic;
  word-break: break-word;
}
.rms-comment-body {
  font-size: 13px;
  white-space: pre-wrap;
  word-break: break-word;
  margin-bottom: 6px;
}
.rms-comment-thread {
  border-top: 1px dashed #dee2e6;
  padding-top: 6px;
  margin-top: 6px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.rms-comment-reply {
  background: #f1f3f5;
  border-radius: 4px;
  padding: 6px 8px;
}
.rms-comment-reply--report-review {
  background: #fdeaea;
  border: 1px solid #f7caca;
}
.rms-comment-card--resolved .rms-comment-reply.rms-comment-reply--report-review {
  background: #fdeaea;
  border: 1px solid #f7caca;
}
.rms-comment-reply-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0px;
  font-size: 11px;
  color: #495057;
  margin-bottom: 8px;
}
.rms-comment-reply-author {
  font-weight: 600;
  color: #212529;
}
.rms-comment-reply-meta {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  color: #868e96;
  font-weight: 500;
}
.rms-comment-reply-date {
  white-space: nowrap;
}
.rms-comment-reply-body {
  font-size: 13px;
  white-space: pre-wrap;
  word-break: break-word;
}
.rms-comment-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 6px;
}
.rms-comment-reply-form {
  margin-top: 6px;
}
.rms-comment-edit-input {
  width: 100%;
  min-height: 72px;
  padding: 6px 8px;
  font-size: 13px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-family: inherit;
  box-sizing: border-box;
  resize: vertical;
  margin-bottom: 6px;
}
.rms-comment-error {
  padding: 12px;
  margin: 8px;
  background: #fff5f5;
  border: 1px solid #fa5252;
  border-radius: 4px;
  color: #c92a2a;
  font-size: 13px;
}
.rms-comment-readonly-badge {
  display: inline-block;
  padding: 2px 8px;
  background: #e9ecef;
  color: #495057;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  margin: 0 0 8px;
}
`;

export function injectStylesheetOnce(targetDocument) {
  // Test-friendly: accept an explicit document for SSR / test environments.
  const doc = targetDocument || (typeof document !== 'undefined' ? document : null);
  if (!doc) return false;
  const existing = doc.getElementById(STYLE_TAG_ID);
  if (existing) {
    if (existing.textContent !== STYLESHEET) {
      existing.textContent = STYLESHEET;
      return true;
    }
    return false;
  }
  const styleEl = doc.createElement('style');
  styleEl.id = STYLE_TAG_ID;
  styleEl.type = 'text/css';
  styleEl.appendChild(doc.createTextNode(STYLESHEET));
  doc.head.appendChild(styleEl);
  return true;
}

/* ---------------------------------------------------------------------------
 * Custom Quill blot — one-time registration (guarded)
 * ------------------------------------------------------------------------- */

let _blotRegistered = false;
export function registerCommentBlotOnce(QuillClass) {
  if (_blotRegistered) return false;
  const Q = QuillClass || Quill;
  if (!Q || typeof Q.register !== 'function' || typeof Q.import !== 'function') {
    return false;
  }
  const Inline = Q.import('blots/inline');
  class RmsCommentBlot extends Inline {
    static create(value) {
      const node = super.create();
      if (value) node.setAttribute('data-comment-id', String(value));
      node.classList.add(BLOT_CLASS);
      return node;
    }
    static formats(node) {
      return node.getAttribute('data-comment-id') || true;
    }
    format(name, value) {
      if (name === BLOT_NAME && value) {
        this.domNode.setAttribute('data-comment-id', String(value));
      } else {
        super.format(name, value);
      }
    }
    formats() {
      const fmts = super.formats();
      fmts[BLOT_NAME] = RmsCommentBlot.formats(this.domNode);
      return fmts;
    }
  }
  RmsCommentBlot.blotName = BLOT_NAME;
  RmsCommentBlot.tagName = 'span';
  RmsCommentBlot.className = BLOT_CLASS;
  try {
    Q.register(`formats/${BLOT_NAME}`, RmsCommentBlot, true);
    _blotRegistered = true;
    return true;
  } catch (e) {
    return false;
  }
}

// Eagerly register at module load so all editor instances pick it up.
registerCommentBlotOnce(Quill);
injectStylesheetOnce();

/* ---------------------------------------------------------------------------
 * Pure helpers — exported for unit testing
 * ------------------------------------------------------------------------- */

/**
 * Transform a single position through a Quill-style delta.
 * Equivalent to quill-delta's `Delta#transformPosition`.
 * @param {number} index - Position in the source document.
 * @param {{ops: Array}} delta - Quill delta describing the change.
 * @param {boolean} [priority=false] - When true, insertions exactly at the
 *   transformed position do NOT push the position forward.
 * @returns {number} Transformed position (clamped to >= 0).
 */
export function transformPosition(index, delta, priority = false) {
  if (!delta || !Array.isArray(delta.ops)) return Math.max(0, index);
  let offset = 0;
  let idx = index;
  for (const op of delta.ops) {
    if (offset > idx) break;
    const insertLen = op.insert !== undefined
      ? (typeof op.insert === 'string' ? op.insert.length : 1)
      : 0;
    if (op.delete !== undefined && typeof op.delete === 'number') {
      const consumed = Math.min(op.delete, idx - offset);
      if (consumed > 0) idx -= consumed;
      // delete does NOT advance offset
      continue;
    }
    if (op.insert !== undefined) {
      if (offset < idx || !priority) {
        idx += insertLen;
      }
      offset += insertLen;
      continue;
    }
    if (typeof op.retain === 'number') {
      offset += op.retain;
    }
  }
  return Math.max(0, idx);
}

/**
 * Transform a Quill range ({index, length}) through a delta.
 * Start uses priority=false (insert at start shifts the range forward).
 * End uses priority=true (insert at end does NOT extend the range).
 */
export function transformRangeThroughDelta(range, delta) {
  if (!range) return range;
  if (!delta || !Array.isArray(delta.ops) || delta.ops.length === 0) {
    return { index: range.index, length: range.length };
  }
  const newStart = transformPosition(range.index, delta, false);
  const newEnd = transformPosition(range.index + range.length, delta, true);
  return {
    index: newStart,
    length: Math.max(0, newEnd - newStart),
  };
}

/** Random-ish id generator that does not require crypto. */
let _idCounter = 0;
export function generateId(prefix) {
  _idCounter += 1;
  const rand = Math.random().toString(36).slice(2, 10);
  return `${prefix || 'id'}-${Date.now().toString(36)}-${_idCounter}-${rand}`;
}

/** Create an immutable audit entry for a state change. */
export function createAuditEntry({
  action,
  authorName,
  authorRole,
  authorBadge,
  commentId,
  replyId,
  timestamp,
}) {
  const entry = {
    id: generateId('audit'),
    action,
    authorName,
    authorRole,
    authorBadge,
    commentId: commentId || null,
    replyId: replyId || null,
    timestamp: timestamp || new Date().toISOString(),
  };
  return Object.freeze(entry);
}

export function filterCommentsByStatus(comments, filter) {
  if (!Array.isArray(comments)) return [];
  if (!filter || filter === FILTER_ALL) return comments;
  return comments.filter((c) => c && c.status === filter);
}

export function countCommentsByStatus(comments) {
  const counts = {
    [FILTER_ALL]: 0,
    [STATUS_OPEN]: 0,
    [STATUS_ACCEPTED]: 0,
    [STATUS_RESOLVED]: 0,
  };
  if (!Array.isArray(comments)) return counts;
  for (const c of comments) {
    if (!c) continue;
    counts[FILTER_ALL] += 1;
    if (counts[c.status] !== undefined) counts[c.status] += 1;
  }
  return counts;
}

export function sortCommentsByDocumentOrder(comments) {
  if (!Array.isArray(comments)) return [];
  return [...comments].sort((a, b) => {
    const ai = a?.range?.index ?? 0;
    const bi = b?.range?.index ?? 0;
    if (ai !== bi) return ai - bi;
    const at = a?.timestamp || '';
    const bt = b?.timestamp || '';
    return at.localeCompare(bt);
  });
}

/**
 * Validate whether a given role can transition a comment from one status to
 * another. Returns the new status if valid, null otherwise.
 */
export function canTransition({ currentStatus, action, role }) {
  if (action === ACTION_ACCEPT) {
    if (role !== ROLE_OFFICER) return null;
    if (currentStatus !== STATUS_OPEN) return null;
    return STATUS_ACCEPTED;
  }
  if (action === ACTION_RESOLVE) {
    if (role !== ROLE_SUPERVISOR) return null;
    if (currentStatus !== STATUS_OPEN && currentStatus !== STATUS_ACCEPTED) return null;
    return STATUS_RESOLVED;
  }
  if (action === ACTION_REOPEN) {
    if (role !== ROLE_SUPERVISOR) return null;
    if (currentStatus !== STATUS_RESOLVED) return null;
    return STATUS_OPEN;
  }
  return null;
}

/* ---------------------------------------------------------------------------
 * ErrorBoundary
 * ------------------------------------------------------------------------- */

class CommentSystemErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    // Surface to host app if a handler was provided.
    if (typeof this.props.onError === 'function') {
      try { this.props.onError(error, info); } catch (e) { /* swallow */ }
    }
  }
  render() {
    if (this.state.error) {
      return (
        <div className="rms-comment-error" role="alert">
          <strong>Comment system error:</strong>{' '}
          {this.state.error.message || 'An unexpected error occurred.'}
        </div>
      );
    }
    return this.props.children;
  }
}
CommentSystemErrorBoundary.propTypes = {
  children: PropTypes.node,
  onError: PropTypes.func,
};

/* ---------------------------------------------------------------------------
 * Inline SVG icons (no lucide dependency)
 * ------------------------------------------------------------------------- */

const Icon = ({ name, size = 14, ...rest }) => {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': 'true',
    focusable: 'false',
    ...rest,
  };
  switch (name) {
    case 'plus':
      return <svg {...common}><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>;
    case 'message':
      return <svg {...common}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>;
    case 'check':
      return <svg {...common}><polyline points="20 6 9 17 4 12" /></svg>;
    case 'rotate':
      return <svg {...common}><polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" /></svg>;
    case 'reply':
      return <svg {...common}><polyline points="9 17 4 12 9 7" /><path d="M20 18v-2a4 4 0 0 0-4-4H4" /></svg>;
    case 'edit':
      return <svg {...common}><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>;
    case 'trash':
      return <svg {...common}><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4h6v2" /></svg>;
    case 'close':
      return <svg {...common}><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>;
    default:
      return null;
  }
};
Icon.propTypes = { name: PropTypes.string.isRequired, size: PropTypes.number };

/* ---------------------------------------------------------------------------
 * Sub-component: Filter chips
 * ------------------------------------------------------------------------- */

function FilterBar({ activeFilter, counts, onChange }) {
  const chips = useMemo(() => ([
    { id: FILTER_ALL, label: 'All' },
    { id: FILTER_OPEN, label: 'Open' },
    { id: FILTER_ACCEPTED, label: 'Accepted' },
    { id: FILTER_RESOLVED, label: 'Resolved' },
  ]), []);
  return (
    <div className="rms-comment-filter-bar" role="group" aria-label="Filter comments">
      {chips.map((c) => {
        const isActive = activeFilter === c.id;
        return (
          <button
            key={c.id}
            type="button"
            className="rms-comment-filter-chip"
            aria-pressed={isActive}
            aria-label={`Show ${c.label.toLowerCase()} comments, ${counts[c.id] || 0} total`}
            onClick={() => onChange(c.id)}
            data-testid={`rms-filter-${c.id}`}
          >
            <span>{c.label}</span>
            <span className="rms-comment-filter-count" data-testid={`rms-filter-count-${c.id}`}>
              {counts[c.id] || 0}
            </span>
          </button>
        );
      })}
    </div>
  );
}
FilterBar.propTypes = {
  activeFilter: PropTypes.oneOf([FILTER_ALL, FILTER_OPEN, FILTER_ACCEPTED, FILTER_RESOLVED]).isRequired,
  counts: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
};

/* ---------------------------------------------------------------------------
 * Sub-component: Reply form
 * ------------------------------------------------------------------------- */

function ReplyForm({
  onSubmit,
  onCancel,
  disabled,
  showButtons,
  value,
  onValueChange,
}) {
  const [text, setText] = useState('');
  const effectiveText = typeof value === 'string' ? value : text;
  const setEffectiveText = useCallback((next) => {
    if (typeof onValueChange === 'function') {
      onValueChange(next);
      return;
    }
    setText(next);
  }, [onValueChange]);
  const stopBubble = useCallback((e) => {
    e.stopPropagation();
  }, []);
  const handleSubmit = useCallback(() => {
    const trimmed = effectiveText.trim();
    if (!trimmed || disabled) return;
    onSubmit(trimmed);
    setEffectiveText('');
  }, [effectiveText, onSubmit, disabled, setEffectiveText]);
  const handleKey = useCallback((e) => {
    e.stopPropagation();
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  }, [handleSubmit, onCancel]);
  return (
    <div
      className="rms-comment-reply-form"
      onMouseDown={stopBubble}
      onClick={stopBubble}
    >
      <textarea
        className="rms-comment-reply-input"
        aria-label="Reply to comment"
        placeholder="Reply… (Ctrl+Enter to submit)"
        value={effectiveText}
        onChange={(e) => setEffectiveText(e.target.value.slice(0, COMMENT_MAX_LENGTH))}
        onKeyDown={handleKey}
        disabled={disabled}
        maxLength={COMMENT_MAX_LENGTH}
        data-testid="rms-reply-input"
      />
      <CharCount value={effectiveText} max={COMMENT_MAX_LENGTH} />
      {showButtons ? (
        <div className="rms-comment-reply-buttons">
          <button
            type="button"
            className="rms-btn rms-btn--ghost"
            onClick={onCancel}
            data-testid="rms-reply-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            className="rms-btn rms-btn--primary"
            onClick={handleSubmit}
            disabled={disabled || !effectiveText.trim()}
            data-testid="rms-reply-submit"
          >
          Send
          </button>
        </div>
      ) : null}
    </div>
  );
}
ReplyForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  showButtons: PropTypes.bool,
  value: PropTypes.string,
  onValueChange: PropTypes.func,
};
ReplyForm.defaultProps = {
  showButtons: true,
  value: undefined,
  onValueChange: undefined,
};

/* ---------------------------------------------------------------------------
 * Sub-component: Comment card
 * ------------------------------------------------------------------------- */

function CommentCard({
  comment,
  isActive,
  currentUser,
  readOnly,
  actionMode,
  formatTimestamp,
  onActivate,
  onAccept,
  onResolve,
  onReopen,
  onReply,
  onEdit,
  onDelete,
}) {
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyBody, setReplyBody] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editBody, setEditBody] = useState(comment.body || '');
  const role = currentUser?.role;
  const status = comment.status;
  const isReportReviewMode = actionMode === 'reportReview';

  const showAccept = !readOnly && role === ROLE_OFFICER && status === STATUS_OPEN;
  const showResolve = !isReportReviewMode && !readOnly && role === ROLE_SUPERVISOR && (status === STATUS_OPEN || status === STATUS_ACCEPTED);
  // Reopen should appear only in Report Review flow.
  const showReopen = isReportReviewMode && !readOnly && role === ROLE_SUPERVISOR && status === STATUS_RESOLVED;
  const showReply = !readOnly && (!isReportReviewMode ? status !== STATUS_RESOLVED : status === STATUS_RESOLVED);
  const showEdit = isReportReviewMode && !readOnly && status !== STATUS_RESOLVED;
  const showDelete = isReportReviewMode && !readOnly;

  const handleAccept = useCallback(() => onAccept(comment.id), [onAccept, comment.id]);
  const handleResolve = useCallback(() => onResolve(comment.id), [onResolve, comment.id]);
  const handleReopen = useCallback(() => onReopen(comment.id), [onReopen, comment.id]);
  const handleDelete = useCallback(() => onDelete(comment.id), [onDelete, comment.id]);
  const handleReplySubmit = useCallback((body) => {
    onReply(comment.id, body);
    setReplyBody('');
    setReplyOpen(false);
  }, [comment.id, onReply]);
  const handleEditSubmit = useCallback(() => {
    const trimmed = String(editBody || '').trim();
    if (!trimmed) return;
    onEdit(comment.id, trimmed);
    setEditOpen(false);
  }, [comment.id, editBody, onEdit]);

  const handleKey = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onActivate(comment.id);
    }
  }, [onActivate, comment.id]);

  const cardClass = [
    'rms-comment-card',
    `rms-comment-card--${status}`,
    isActive ? 'rms-comment-card--active' : '',
  ].filter(Boolean).join(' ');

  return (
    <div
      className={cardClass}
      role="button"
      tabIndex={0}
      aria-label={`Comment by ${comment.authorName}, status ${status}`}
      aria-current={isActive ? 'true' : undefined}
      onClick={() => onActivate(comment.id)}
      onKeyDown={handleKey}
      data-testid={`rms-card-${comment.id}`}
    >
      <div className="rms-comment-header">
        <div>
          <div className="rms-comment-author" data-testid={`rms-card-author-${comment.id}`}>
            {comment.authorName}
          </div>
          <div className="rms-comment-meta">
            <span data-testid={`rms-card-role-${comment.id}`}>{comment.authorRole}</span>
            <span aria-label="Badge number" data-testid={`rms-card-badge-${comment.id}`}>
              #{comment.authorBadge}
            </span>
            <span aria-label="Created at" data-testid={`rms-card-timestamp-${comment.id}`}>
              {formatTimestamp(comment.timestamp)}
            </span>
          </div>
        </div>
        <span
          className={`rms-comment-status-pill rms-comment-status-pill--${status}`}
          data-testid={`rms-card-status-${comment.id}`}
        >
          {status}
        </span>
      </div>
      {comment.quotedText ? (
        <div className="rms-comment-quote" data-testid={`rms-card-quote-${comment.id}`}>
          “{comment.quotedText}”
        </div>
      ) : null}
      {editOpen ? (
        <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
          <textarea
            className="rms-comment-edit-input w-100"
            value={editBody}
            onChange={(e) => setEditBody(e.target.value.slice(0, COMMENT_MAX_LENGTH))}
            onKeyDown={(e) => e.stopPropagation()}
            placeholder="Edit comment..."
            aria-label="Edit comment"
            maxLength={COMMENT_MAX_LENGTH}
            data-testid={`rms-edit-input-${comment.id}`}
          />
          <CharCount value={editBody} max={COMMENT_MAX_LENGTH} />
        </div>
      ) : (
        <div className="rms-comment-body" data-testid={`rms-card-body-${comment.id}`}>
          {comment.body}
        </div>
      )}

      {Array.isArray(comment.replies) && comment.replies.length > 0 ? (
        <div className="rms-comment-thread" data-testid={`rms-card-thread-${comment.id}`}>
          {comment.replies.map((r) => (
            <div
              className={`rms-comment-reply ${r?.sourceMode === 'reportReview' ? 'rms-comment-reply--report-review' : ''}`}
              key={r.id}
              data-testid={`rms-reply-${r.id}`}
            >
              <div className="rms-comment-reply-header">
                <span className="rms-comment-reply-author">{r.authorName}</span>
                <span className="rms-comment-reply-meta">
                  <span>{`${r.authorRole} #${r.authorBadge}`}</span>
                  <span className="rms-comment-reply-date">{formatTimestamp(r.timestamp)}</span>
                </span>
              </div>
              <div className="rms-comment-reply-body">{r.body}</div>
            </div>
          ))}
        </div>
      ) : null}

      <div className="rms-comment-actions" onClick={(e) => e.stopPropagation()}>
        {showAccept ? (
          <button
            type="button"
            className="rms-btn rms-btn--success"
            onClick={handleAccept}
            data-testid={`rms-accept-${comment.id}`}
          >
            <Icon name="check" /> Accept
          </button>
        ) : null}
        {showResolve && !replyOpen ? (
          <button
            type="button"
            className="rms-btn rms-btn--primary"
            onClick={handleResolve}
            data-testid={`rms-resolve-${comment.id}`}
          >
            <Icon name="check" /> Resolve
          </button>
        ) : null}
        {showReopen ? (
          <button
            type="button"
            className="rms-btn rms-btn--ghost"
            onClick={handleReopen}
            data-testid={`rms-reopen-${comment.id}`}
          >
            <Icon name="rotate" /> Reopen
          </button>
        ) : null}
        {showReply && !replyOpen ? (
          <button
            type="button"
            className="rms-btn"
            onClick={() => {
              setReplyOpen((v) => !v);
              setReplyBody('');
            }}
            data-testid={`rms-reply-toggle-${comment.id}`}
            aria-expanded={replyOpen}
          >
            <Icon name="reply" /> Reply
          </button>
        ) : null}
        {showEdit ? (
          editOpen ? (
            <>
              <button
                type="button"
                className="rms-btn rms-btn--ghost"
                onClick={() => {
                  setEditOpen(false);
                  setEditBody(comment.body || '');
                }}
                data-testid={`rms-edit-cancel-${comment.id}`}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rms-btn rms-btn--success"
                onClick={handleEditSubmit}
                disabled={!String(editBody || '').trim()}
                data-testid={`rms-edit-save-${comment.id}`}
              >
                Save
              </button>
            </>
          ) : (
          <button
            type="button"
            className="rms-btn rms-btn--warning"
            onClick={() => {
              setEditOpen((prev) => !prev);
              setEditBody(comment.body || '');
              setReplyOpen(false);
            }}
            data-testid={`rms-edit-${comment.id}`}
          >
            <Icon name="edit" /> Edit
          </button>
          )
        ) : null}
        {showDelete && !editOpen ? (
          <button
            type="button"
            className="rms-btn rms-btn--danger"
            onClick={handleDelete}
            data-testid={`rms-delete-${comment.id}`}
          >
            <Icon name="trash" /> Delete
          </button>
        ) : null}
      </div>
      {replyOpen ? (
        <ReplyForm
          onSubmit={handleReplySubmit}
          onCancel={() => {
            setReplyOpen(false);
            setReplyBody('');
          }}
          disabled={readOnly}
          showButtons={true}
          value={replyBody}
          onValueChange={setReplyBody}
        />
      ) : null}
    </div>
  );
}

const replyShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  body: PropTypes.string.isRequired,
  authorName: PropTypes.string.isRequired,
  authorRole: PropTypes.oneOf([ROLE_SUPERVISOR, ROLE_OFFICER]).isRequired,
  authorBadge: PropTypes.string.isRequired,
  timestamp: PropTypes.string.isRequired,
});

const commentShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  body: PropTypes.string.isRequired,
  quotedText: PropTypes.string,
  range: PropTypes.shape({
    index: PropTypes.number.isRequired,
    length: PropTypes.number.isRequired,
  }).isRequired,
  status: PropTypes.oneOf([STATUS_OPEN, STATUS_ACCEPTED, STATUS_RESOLVED]).isRequired,
  authorName: PropTypes.string.isRequired,
  authorRole: PropTypes.oneOf([ROLE_SUPERVISOR, ROLE_OFFICER]).isRequired,
  authorBadge: PropTypes.string.isRequired,
  timestamp: PropTypes.string.isRequired,
  replies: PropTypes.arrayOf(replyShape),
});

CommentCard.propTypes = {
  comment: commentShape.isRequired,
  isActive: PropTypes.bool,
  currentUser: PropTypes.shape({
    name: PropTypes.string.isRequired,
    role: PropTypes.oneOf([ROLE_SUPERVISOR, ROLE_OFFICER]).isRequired,
    badge: PropTypes.string.isRequired,
  }).isRequired,
  readOnly: PropTypes.bool,
  formatTimestamp: PropTypes.func.isRequired,
  onActivate: PropTypes.func.isRequired,
  onAccept: PropTypes.func.isRequired,
  onResolve: PropTypes.func.isRequired,
  onReopen: PropTypes.func.isRequired,
  onReply: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  actionMode: PropTypes.oneOf(['default', 'reportReview']),
};

/* ---------------------------------------------------------------------------
 * Main component
 * ------------------------------------------------------------------------- */

const InnerEditor = forwardRef(function InnerEditor(props, ref) {
  const {
    currentUser,
    reportId,
    initialContent,
    initialComments,
    onSave,
    onContentChange,
    readOnly,
    contentReadOnly,
    actionMode,
    allowInlineCommentCreation,
  } = props;
  const isFullyReadOnly = !!readOnly;
  const isContentReadOnlyMode = !!contentReadOnly;
  // In report review mode, narrative content stays immutable but supervisors
  // can still select text to anchor new comments.
  const canCreateCommentsOnReadOnlyContent = actionMode === 'reportReview';
  const disableInlineCommentCreation = isFullyReadOnly || !allowInlineCommentCreation || (isContentReadOnlyMode && !canCreateCommentsOnReadOnlyContent);
  const isEditorReadOnly = isFullyReadOnly || (isContentReadOnlyMode && !canCreateCommentsOnReadOnlyContent);

  const {
    datezone,
    GetDataTimeZone,
    LoginAgencyID,
    localStoreArray,
  } = useContext(AgencyContext) || {};

  const agencyId = LoginAgencyID || localStoreArray?.AgencyID;

  const quillRef = useRef(null);
  const editorWrapRef = useRef(null);
  const popoverInputRef = useRef(null);
  const popoverRef = useRef(null);

  useEffect(() => {
    if (datezone || !agencyId || typeof GetDataTimeZone !== 'function') return;
    GetDataTimeZone(agencyId);
  }, [datezone, agencyId, GetDataTimeZone]);

  const formatCommentTimestamp = useCallback(
    (ts) => formatAgencyTimelineDateTime(ts),
    [],
  );

  const commentTimestampIso = useCallback(
    () => getAgencyWallClockTimestamp(datezone),
    [datezone],
  );

  const [content, setContent] = useState(initialContent || '');
  const [comments, setComments] = useState(() => {
    return Array.isArray(initialComments)
      ? initialComments.map((c) => ({
          ...c,
          replies: Array.isArray(c.replies) ? c.replies.slice() : [],
        }))
      : [];
  });
  const [audit, setAudit] = useState([]);
  const [activeFilter, setActiveFilter] = useState(FILTER_ALL);
  const [activeCommentId, setActiveCommentId] = useState(null);
  const [pendingSelection, setPendingSelection] = useState(null);
  const [floatingPos, setFloatingPos] = useState(null);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [popoverPos, setPopoverPos] = useState(null);
  const [draftBody, setDraftBody] = useState('');
  const [boundaryError, setBoundaryError] = useState(null); // unused but kept for parity

  /* -------- Inject styles in case a non-default document is used (HMR) ---- */
  useEffect(() => {
    injectStylesheetOnce();
  }, []);

  /* -------- Notify host on every state-changing action ------------------- */
  const notifySave = useCallback((nextComments, nextAudit, nextContent) => {
    if (typeof onSave === 'function') {
      try {
        onSave(nextContent, nextComments, nextAudit);
      } catch (err) {
        // Host save failure must not break the editor. Audit is preserved.
        // Consumers should handle their own error display.
      }
    }
  }, [onSave]);

  /* -------- Re-paint highlights whenever comments change ---------------- */
  const repaintHighlights = useCallback((targetComments) => {
    const quill = quillRef.current && quillRef.current.getEditor && quillRef.current.getEditor();
    if (!quill || typeof quill.formatText !== 'function') return;
    const list = Array.isArray(targetComments) ? targetComments : comments;
    // First, clear existing comment formatting across the doc to avoid stale spans.
    try {
      const totalLen = typeof quill.getLength === 'function' ? quill.getLength() : 0;
      if (totalLen > 0) {
        quill.formatText(0, totalLen, BLOT_NAME, false, 'silent');
      }
    } catch (e) { /* tolerate */ }
    // Apply current comment ranges.
    for (const c of list) {
      if (!c || !c.range) continue;
      if (c.status === STATUS_RESOLVED) continue; // hidden highlight when resolved
      try {
        quill.formatText(c.range.index, c.range.length, BLOT_NAME, c.id, 'silent');
      } catch (e) { /* tolerate out-of-range comments */ }
    }
  }, [comments]);

  useLayoutEffect(() => {
    repaintHighlights(comments);
  }, [comments, repaintHighlights]);

  /* -------- Handle Quill selection-change ------------------------------- */
  const handleChangeSelection = useCallback((range, _source, _editor) => {
    if (disableInlineCommentCreation) return;
    if (!range || range.length === 0) {
      setFloatingPos(null);
      // Keep selection anchor while comment popover is open; focusing the
      // textarea moves focus away from Quill and emits an empty selection.
      if (!popoverOpen) {
        setPendingSelection(null);
      }
      return;
    }
    if (currentUser?.role !== ROLE_SUPERVISOR) {
      setFloatingPos(null);
      setPendingSelection(null);
      return;
    }
    const quill = quillRef.current && quillRef.current.getEditor && quillRef.current.getEditor();
    if (!quill) return;
    let text = '';
    try { text = quill.getText(range.index, range.length) || ''; } catch (e) { text = ''; }
    let bounds = { left: 0, top: 0, width: 0, height: 0 };
    try { bounds = quill.getBounds(range.index, range.length) || bounds; } catch (e) { /* tolerate */ }
    setPendingSelection({ index: range.index, length: range.length, text });
    setFloatingPos({
      left: Math.max(0, bounds.left),
      top: Math.max(0, bounds.top + bounds.height + 4),
    });
  }, [disableInlineCommentCreation, currentUser, popoverOpen]);

  /* -------- Handle Quill text-change: track ranges through delta -------- */
  const handleChangeContent = useCallback((value, delta, source) => {
    if ((isContentReadOnlyMode || isFullyReadOnly) && source === 'user') {
      // Read-only narrative mode: block every mutation (letters, words,
      // spaces, digits, symbols, deletes, embeds, or formatting changes).
      const ops = Array.isArray(delta?.ops) ? delta.ops : [];
      const insertedText = ops
        .filter((op) => typeof op?.insert === 'string')
        .map((op) => op.insert)
        .join('');
      const blockAllInputRegex = /[\s\S]/;
      const hasForbiddenText = insertedText.length > 0 && blockAllInputRegex.test(insertedText);
      const hasDelete = ops.some((op) => Number(op?.delete) > 0);
      const hasEmbedInsert = ops.some((op) => op?.insert && typeof op.insert === 'object');
      const hasFormatChange = ops.some((op) => op?.attributes && Object.keys(op.attributes).length > 0);

      if (hasForbiddenText || hasDelete || hasEmbedInsert || hasFormatChange) {
        // Hard rollback only the latest attempted user mutation while keeping
        // the already loaded narrative content intact.
        const quill = quillRef.current && quillRef.current.getEditor && quillRef.current.getEditor();
        if (quill) {
          try {
            if (quill.history && typeof quill.history.undo === 'function') {
              quill.history.undo();
            }
          } catch (e) { /* tolerate */ }
        }
      }
      return;
    }
    setContent(value);
    if (source !== 'user') return;
    // Notify parent that the narrative text was directly edited so it can
    // persist the change independently of comment lifecycle events.
    if (typeof onContentChange === 'function') {
      onContentChange(value);
    }
    if (!delta || !Array.isArray(delta.ops)) return;
    setComments((prev) => {
      let mutated = false;
      const next = prev.map((c) => {
        if (!c?.range) return c;
        const transformed = transformRangeThroughDelta(c.range, delta);
        if (transformed.index === c.range.index && transformed.length === c.range.length) {
          return c;
        }
        mutated = true;
        return { ...c, range: transformed };
      });
      if (mutated) {
        // We do not push an audit entry for editor edits — only for comment
        // lifecycle actions, per the spec.
        notifySave(next, audit, value);
        return next;
      }
      return prev;
    });
  }, [audit, notifySave, isContentReadOnlyMode, isFullyReadOnly, content]);

  /* -------- Floating "Add comment" button click ------------------------ */
  const openCommentPopover = useCallback(() => {
    if (disableInlineCommentCreation) return;
    if (!pendingSelection) return;
    if (currentUser?.role !== ROLE_SUPERVISOR) return;
    // Convert editor-relative coords to viewport-absolute so the popover can
    // use `position: fixed` and won't clip against the editor container.
    const wrapRect = editorWrapRef.current?.getBoundingClientRect?.() ?? { left: 0, top: 0 };
    setPopoverOpen(true);
    setPopoverPos(
      floatingPos
        ? { left: wrapRect.left + floatingPos.left, top: wrapRect.top + floatingPos.top + 28 }
        : { left: wrapRect.left + 10, top: wrapRect.top + 40 },
    );
    setDraftBody('');
  }, [disableInlineCommentCreation, pendingSelection, currentUser, floatingPos]);

  useEffect(() => {
    if (popoverOpen && popoverInputRef.current) {
      try { popoverInputRef.current.focus(); } catch (e) { /* tolerate */ }
    }
  }, [popoverOpen]);

  /* -------- Smart-position: keep popover inside the viewport ----------- */
  useLayoutEffect(() => {
    if (!popoverOpen || !popoverPos || !popoverRef.current) return;
    const el = popoverRef.current;
    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth || document.documentElement.clientWidth;
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const MARGIN = 8;
    const POPOVER_H = rect.height || 240;
    const POPOVER_W = rect.width || 320;

    let { left, top } = popoverPos;

    // Flip left if too far right
    if (left + POPOVER_W + MARGIN > vw) {
      left = Math.max(MARGIN, vw - POPOVER_W - MARGIN);
    }
    if (left < MARGIN) left = MARGIN;

    // Flip above if too close to bottom
    if (top + POPOVER_H + MARGIN > vh) {
      // Show above the floating button (button is ~28px above popoverPos.top)
      top = Math.max(MARGIN, popoverPos.top - POPOVER_H - 36);
    }
    if (top < MARGIN) top = MARGIN;

    if (left !== popoverPos.left || top !== popoverPos.top) {
      setPopoverPos({ left, top });
    }
  }, [popoverOpen, popoverPos]);

  /* -------- Submit new comment ----------------------------------------- */
  const submitNewComment = useCallback(() => {
    const trimmed = (draftBody || '').trim();
    if (!trimmed) return;
    if (!pendingSelection) return;
    if (currentUser?.role !== ROLE_SUPERVISOR) return;

    const newComment = {
      id: generateId('comment'),
      body: trimmed,
      quotedText: pendingSelection.text,
      range: { index: pendingSelection.index, length: pendingSelection.length },
      status: STATUS_OPEN,
      authorName: currentUser.name,
      authorRole: currentUser.role,
      authorBadge: currentUser.badge,
      timestamp: commentTimestampIso(),
      replies: [],
    };
    const auditEntry = createAuditEntry({
      action: ACTION_CREATE,
      authorName: currentUser.name,
      authorRole: currentUser.role,
      authorBadge: currentUser.badge,
      commentId: newComment.id,
    });

    setComments((prev) => {
      const next = [...prev, newComment];
      setAudit((prevAudit) => {
        const nextAudit = [...prevAudit, auditEntry];
        notifySave(next, nextAudit, content);
        return nextAudit;
      });
      return next;
    });
    setPopoverOpen(false);
    setPopoverPos(null);
    setDraftBody('');
    setFloatingPos(null);
    setPendingSelection(null);
    setActiveCommentId(newComment.id);
  }, [draftBody, pendingSelection, currentUser, content, notifySave, commentTimestampIso]);

  /* -------- Reply ------------------------------------------------------- */
  const handleReply = useCallback((commentId, body) => {
    if (!body || !body.trim()) return;
    if (!currentUser) return;
    const trimmed = body.trim();
    const shouldReopenOnReply = actionMode === 'reportReview';
    const reply = {
      id: generateId('reply'),
      body: trimmed,
      authorName: currentUser.name,
      authorRole: currentUser.role,
      authorBadge: currentUser.badge,
      timestamp: commentTimestampIso(),
      sourceMode: shouldReopenOnReply ? 'reportReview' : 'default',
    };
    const auditEntry = createAuditEntry({
      action: ACTION_REPLY,
      authorName: currentUser.name,
      authorRole: currentUser.role,
      authorBadge: currentUser.badge,
      commentId,
      replyId: reply.id,
    });
    setComments((prev) => {
      const originalComment = prev.find((c) => c.id === commentId);
      const canReopenFromReply = shouldReopenOnReply && originalComment?.status === STATUS_RESOLVED;
      const next = prev.map((c) => {
        if (c.id !== commentId) return c;
        return {
          ...c,
          status: canReopenFromReply ? STATUS_OPEN : c.status,
          replies: [...(c.replies || []), reply],
        };
      });
      setAudit((prevAudit) => {
        const nextAudit = [...prevAudit];
        if (canReopenFromReply) {
          nextAudit.push(createAuditEntry({
            action: ACTION_REOPEN,
            authorName: currentUser.name,
            authorRole: currentUser.role,
            authorBadge: currentUser.badge,
            commentId,
          }));
        }
        nextAudit.push(auditEntry);
        notifySave(next, nextAudit, content);
        return nextAudit;
      });
      return next;
    });
  }, [currentUser, content, notifySave, actionMode, commentTimestampIso]);

  /* -------- State transitions: accept / resolve / reopen -------------- */
  const transition = useCallback((commentId, action) => {
    if (!currentUser) return;
    setComments((prev) => {
      const next = prev.map((c) => {
        if (c.id !== commentId) return c;
        const newStatus = canTransition({
          currentStatus: c.status, action, role: currentUser.role,
        });
        if (!newStatus) return c;
        return { ...c, status: newStatus };
      });
      const target = next.find((c) => c.id === commentId);
      const original = prev.find((c) => c.id === commentId);
      if (target && original && target.status !== original.status) {
        const auditEntry = createAuditEntry({
          action,
          authorName: currentUser.name,
          authorRole: currentUser.role,
          authorBadge: currentUser.badge,
          commentId,
        });
        setAudit((prevAudit) => {
          const nextAudit = [...prevAudit, auditEntry];
          notifySave(next, nextAudit, content);
          return nextAudit;
        });
      }
      return next;
    });
  }, [currentUser, content, notifySave]);

  const handleAccept = useCallback((id) => transition(id, ACTION_ACCEPT), [transition]);
  const handleResolve = useCallback((id) => transition(id, ACTION_RESOLVE), [transition]);
  const handleReopen = useCallback((id) => transition(id, ACTION_REOPEN), [transition]);
  const handleEdit = useCallback((commentId, body) => {
    if (!currentUser) return;
    const trimmed = String(body || '').trim();
    if (!trimmed) return;
    setComments((prev) => {
      const next = prev.map((c) => (c.id === commentId ? { ...c, body: trimmed } : c));
      const changed = prev.some((c) => c.id === commentId && c.body !== trimmed);
      if (changed) {
        const auditEntry = createAuditEntry({
          action: ACTION_EDIT,
          authorName: currentUser.name,
          authorRole: currentUser.role,
          authorBadge: currentUser.badge,
          commentId,
        });
        setAudit((prevAudit) => {
          const nextAudit = [...prevAudit, auditEntry];
          notifySave(next, nextAudit, content);
          return nextAudit;
        });
      }
      return next;
    });
  }, [currentUser, notifySave, content]);
  const handleDelete = useCallback((commentId) => {
    if (!currentUser) return;
    setComments((prev) => {
      const exists = prev.some((c) => c.id === commentId);
      if (!exists) return prev;
      const next = prev.filter((c) => c.id !== commentId);
      const auditEntry = createAuditEntry({
        action: ACTION_DELETE,
        authorName: currentUser.name,
        authorRole: currentUser.role,
        authorBadge: currentUser.badge,
        commentId,
      });
      setAudit((prevAudit) => {
        const nextAudit = [...prevAudit, auditEntry];
        notifySave(next, nextAudit, content);
        return nextAudit;
      });
      if (activeCommentId === commentId) {
        setActiveCommentId(null);
      }
      return next;
    });
  }, [currentUser, notifySave, content, activeCommentId]);

  /* -------- Activate / scroll to comment ------------------------------- */
  const handleActivate = useCallback((commentId) => {
    setActiveCommentId(commentId);
    const c = comments.find((x) => x.id === commentId);
    if (!c) return;
    const quill = quillRef.current && quillRef.current.getEditor && quillRef.current.getEditor();
    if (!quill) return;
    try {
      quill.setSelection(c.range.index, c.range.length, 'user');
      // Quill scrolls into view on setSelection. We also nudge the DOM.
      const wrap = editorWrapRef.current;
      if (wrap) {
        const node = wrap.querySelector(`[data-comment-id="${c.id}"]`);
        if (node && typeof node.scrollIntoView === 'function') {
          node.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    } catch (e) { /* tolerate */ }
  }, [comments]);

  /* -------- Keyboard shortcut Ctrl+Shift+M ----------------------------- */
  useEffect(() => {
    if (disableInlineCommentCreation) return undefined;
    const handler = (e) => {
      const wantsCtrl = KEYBOARD_OPEN_COMMENT.ctrl;
      const wantsShift = KEYBOARD_OPEN_COMMENT.shift;
      const isCtrl = e.ctrlKey || e.metaKey;
      const isShift = e.shiftKey;
      if (!isCtrl || isCtrl !== wantsCtrl) return;
      if (!isShift || isShift !== wantsShift) return;
      const key = String(e.key || '').toUpperCase();
      if (key !== KEYBOARD_OPEN_COMMENT.key) return;
      if (currentUser?.role !== ROLE_SUPERVISOR) return;
      if (!pendingSelection) return;
      e.preventDefault();
      openCommentPopover();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [disableInlineCommentCreation, currentUser, pendingSelection, openCommentPopover]);

  /* -------- Test hooks (do not use in production) --------------------- */
  useImperativeHandle(ref, () => ({
    _test_simulateSelection: (sel) => {
      if (!sel) {
        setPendingSelection(null);
        setFloatingPos(null);
        return;
      }
      if (currentUser?.role !== ROLE_SUPERVISOR) {
        // Officer selection still updates pendingSelection-free state.
        setPendingSelection(null);
        setFloatingPos(null);
        return;
      }
      setPendingSelection({
        index: sel.index || 0,
        length: sel.length || 0,
        text: sel.text || '',
      });
      setFloatingPos({ left: sel.left || 10, top: sel.top || 10 });
    },
    _test_simulateDelta: (delta, source) => {
      handleChangeContent(content, delta, source || 'user');
    },
    _test_getState: () => ({
      comments,
      audit,
      activeFilter,
      activeCommentId,
      pendingSelection,
    }),
    _test_openPopover: () => openCommentPopover(),
  }), [
    currentUser, content, comments, audit, activeFilter, activeCommentId,
    pendingSelection, handleChangeContent, openCommentPopover,
  ]);

  /* -------- Derived data ----------------------------------------------- */
  const orderedComments = useMemo(
    () => sortCommentsByDocumentOrder(comments),
    [comments]
  );
  const filteredComments = useMemo(
    () => filterCommentsByStatus(orderedComments, activeFilter),
    [orderedComments, activeFilter]
  );
  const counts = useMemo(() => countCommentsByStatus(comments), [comments]);

  /* -------- Render ----------------------------------------------------- */

  const quillModules = useMemo(() => ({
    toolbar: (isFullyReadOnly || isContentReadOnlyMode) ? false : [
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['clean'],
    ],
    history: { userOnly: true },
  }), [isFullyReadOnly, isContentReadOnlyMode]);

  const formats = useMemo(() => ([
    'bold', 'italic', 'underline', 'list', BLOT_NAME,
  ]), []);

  return (
    <div
      className={`rms-comment-root ${props.className}`}
      data-testid="rms-root"
      data-report-id={reportId}
    >
      <div className="rms-comment-editor-wrap p-0" ref={editorWrapRef}>
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={content}
          onChange={handleChangeContent}
          onChangeSelection={handleChangeSelection}
          readOnly={isEditorReadOnly}
          modules={quillModules}
          formats={formats}
          placeholder="Begin narrative…"
          data-testid="rms-quill"
        />
        {/* {(isFullyReadOnly || isContentReadOnlyMode) ? (
          <span className="rms-comment-readonly-badge" data-testid="rms-readonly-badge">
            Read-only
          </span>
        ) : null} */}

        {/* Floating "Add comment" button — supervisor only, when text selected */}
        {!disableInlineCommentCreation &&
          floatingPos &&
          pendingSelection &&
          pendingSelection.length > 0 &&
          currentUser?.role === ROLE_SUPERVISOR &&
          !popoverOpen ? (
            <button
              type="button"
              className="rms-comment-floating-btn"
              style={{ left: floatingPos.left, top: floatingPos.top }}
              onClick={openCommentPopover}
              aria-label="Add a comment on the selected text"
              data-testid="rms-add-button"
            >
              <Icon name="plus" />
              <span>Comment</span>
            </button>
          ) : null}

        {/* Popover -- portalled to document.body so it escapes scroll/overflow clipping */}
        {popoverOpen && popoverPos ? createPortal(
          <div
            ref={popoverRef}
            className="rms-comment-popover"
            style={{ left: popoverPos.left, top: popoverPos.top }}
            role="dialog"
            aria-label="Add a comment"
            data-testid="rms-popover"
          >
            <div className="rms-comment-popover-header">
              <div className="rms-comment-popover-title">Add comment</div>
              <button
                type="button"
                className="rms-comment-popover-close"
                onClick={() => { setPopoverOpen(false); setDraftBody(''); }}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            {pendingSelection?.text ? (
              <div className="rms-comment-popover-excerpt" data-testid="rms-popover-excerpt">
                “{pendingSelection.text}”
              </div>
            ) : null}
            <textarea
              ref={popoverInputRef}
              value={draftBody}
              onChange={(e) => setDraftBody(e.target.value.slice(0, COMMENT_MAX_LENGTH))}
              placeholder="Comment text…"
              aria-label="Comment text"
              maxLength={COMMENT_MAX_LENGTH}
              data-testid="rms-popover-input"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                  e.preventDefault();
                  submitNewComment();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  setPopoverOpen(false);
                }
              }}
            />
            <CharCount value={draftBody} max={COMMENT_MAX_LENGTH} />
            <div className="rms-comment-popover-buttons">
              <button
                type="button"
                className="rms-btn rms-btn--ghost"
                onClick={() => { setPopoverOpen(false); setDraftBody(''); }}
                data-testid="rms-popover-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                className="rms-btn rms-btn--primary"
                onClick={submitNewComment}
                disabled={!draftBody.trim()}
                data-testid="rms-popover-submit"
              >
                <Icon name="message" /> Comment
              </button>
            </div>
          </div>,
          document.body,
        ) : null}
      </div>

      {/* Sidebar */}
      <aside
        className="rms-comment-sidebar"
        aria-label="Comments sidebar"
        data-testid="rms-sidebar"
      >
        <FilterBar
          activeFilter={activeFilter}
          counts={counts}
          onChange={setActiveFilter}
        />
        <div className="rms-comment-cards" data-testid="rms-cards">
          {filteredComments.length === 0 ? (
            <div className="rms-comment-empty" data-testid="rms-empty">
              {comments.length === 0 ? 'No comments yet.' : 'No comments match this filter.'}
            </div>
          ) : (
            filteredComments.map((c) => (
              <CommentCard
                key={c.id}
                comment={c}
                isActive={activeCommentId === c.id}
                currentUser={currentUser}
                readOnly={readOnly}
                formatTimestamp={formatCommentTimestamp}
                onActivate={handleActivate}
                onAccept={handleAccept}
                onResolve={handleResolve}
                onReopen={handleReopen}
                onReply={handleReply}
                onEdit={handleEdit}
                onDelete={handleDelete}
                actionMode={actionMode}
              />
            ))
          )}
        </div>
      </aside>
    </div>
  );
});

InnerEditor.propTypes = {
  currentUser: PropTypes.shape({
    name: PropTypes.string.isRequired,
    role: PropTypes.oneOf([ROLE_SUPERVISOR, ROLE_OFFICER]).isRequired,
    badge: PropTypes.string.isRequired,
  }).isRequired,
  reportId: PropTypes.string.isRequired,
  initialContent: PropTypes.string,
  initialComments: PropTypes.arrayOf(commentShape),
  onSave: PropTypes.func,
  onContentChange: PropTypes.func,
  readOnly: PropTypes.bool,
  contentReadOnly: PropTypes.bool,
  actionMode: PropTypes.oneOf(['default', 'reportReview']),
  allowInlineCommentCreation: PropTypes.bool,
};

InnerEditor.defaultProps = {
  initialContent: '',
  initialComments: [],
  onSave: undefined,
  onContentChange: undefined,
  readOnly: false,
  contentReadOnly: false,
  actionMode: 'default',
  allowInlineCommentCreation: true,
};

/* ---------------------------------------------------------------------------
 * Public component: wraps InnerEditor in ErrorBoundary
 * ------------------------------------------------------------------------- */

const RMSCommentEditor = forwardRef(function RMSCommentEditor(props, ref) {
  return (
    <CommentSystemErrorBoundary onError={props.onError}>
      <InnerEditor ref={ref} {...props} />
    </CommentSystemErrorBoundary>
  );
});

RMSCommentEditor.propTypes = {
  ...InnerEditor.propTypes,
  onError: PropTypes.func,
};

RMSCommentEditor.defaultProps = {
  ...InnerEditor.defaultProps,
  onError: undefined,
};

RMSCommentEditor.displayName = 'RMSCommentEditor';

export default RMSCommentEditor;
